import { Component, inject, OnDestroy, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Firestore, collection, collectionData, addDoc, deleteDoc, doc, updateDoc, query, where, getDocs, arrayUnion, arrayRemove } from '@angular/fire/firestore';
import { Storage, ref, uploadBytesResumable, getDownloadURL, deleteObject } from '@angular/fire/storage';
import { Observable, Subscription, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { Store } from '../models/store.model';
import { BrandService } from '../services/brand.service';
import { Brand } from '../models/brand.model';

@Component({
  selector: 'app-company-management',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './company-management.component.html',
  styleUrls: ['./company-management.component.css']
})
export class CompanyManagementComponent implements OnDestroy {
  private firestore = inject(Firestore);
  private storage = inject(Storage);
  private brandService = inject(BrandService);
  companies$: Observable<any[]>;
  owners$: Observable<any[]>;
  brands$: Observable<Brand[]>;
  brands: Brand[] = [];
  owners: any[] = [];
  companies: any[] = [];
  
  // Company Form
  newCompany: any = { company: '', brand: '', pocName: '', email: '', phone: '', ein: '', fedexNumber: '', address: '', salesTax: 0, reserveRate: 0, kcegOwned: false, sis: false, brokerReport: false, brokerName: '', brokerLocation: '', brokerCcRate: null, brokerProfitShare: null, sisCcRate: null, sisProfitShare: null, mallEmail: '', isFeeCollection: false };
  editingId: string | null = null;

  // Expansion & Stores
  expandedCompanyId: string | null = null;
  companyStores$: Observable<Store[]> | null = null;
  companyOwnerships$: Observable<any[]> | null = null;
  newStore: any = { name: '', location: '' };
  isAddingStore = false;

  // Ownership
  currentOwnerships: any[] = [];
  private ownershipsSub: Subscription | null = null;
  newOwnership: any = { ownerId: '', ownershipPercent: 0, profitSharePercent: 0 };
  tempOwnerships: any[] = [];
  errorMessage: string | null = null;
  ownershipErrorMessage: string | null = null;
  generalErrorMessage: string | null = null;
  isModalOpen = false;
  isOwnershipExpanded = true;
  isStoresExpanded = true;
  isSaving = false;

  // SIS Rates
  isSisRatesExpanded = true;
  isBrokerReportExpanded = true;

  // Adjustments
  isAdjustmentsExpanded = true;
  newAdjustment: any = { description: '', amount: null };
  currentAdjustments: any[] = [];
  tempAdjustments: any[] = [];

  // Stores (Modal)
  allStores: Store[] = [];
  availableStores: Store[] = [];
  currentStores: Store[] = [];
  private storesSub: Subscription | null = null;
  private allStoresSub: Subscription | null = null;
  tempStores: Store[] = [];
  storesToDelete: string[] = [];
  newModalStore: Partial<Store> = { name: '', location: '' };

  // File Upload State
  isUploadModalOpen = false;
  uploadingFile = false;
  uploadProgress = 0;
  selectedFiles: File[] = [];
  currentUploadCompany: any = null;
  uploadedFileUrl: string | null = null;
  deletingFileIndex: number | null = null;

  // Sorting
  sortColumn: string = 'company';
  sortDirection: 'asc' | 'desc' = 'asc';

  constructor() {
    const col = collection(this.firestore, 'company');
    this.companies$ = collectionData(col, { idField: 'id' }).pipe(
      map(companies => companies
        .filter(c => !c['isDeleted']))
    );
    this.companies$.subscribe(data => {
      this.companies = data;
      this.sortCompanies();
    });

    this.brands$ = this.brandService.getBrands();
    this.brands$.subscribe(data => this.brands = data);

    const ownersCol = collection(this.firestore, 'owners');
    this.owners$ = collectionData(ownersCol, { idField: 'id' }).pipe(
      map(data => data.sort((a, b) => (a['name'] || '').localeCompare(b['name'] || '')))
    );
    this.owners$.subscribe(data => this.owners = data);

    const storesCol = collection(this.firestore, 'stores');
    this.allStoresSub = collectionData(storesCol, { idField: 'id' }).subscribe(data => {
      this.allStores = (data as Store[])
        .filter(s => !s.isDeleted)
        .sort((a, b) => (a.storeId || '').localeCompare(b.storeId || ''));
      this.updateAvailableStores();
    });
  }

  updateAvailableStores() {
    // Filter stores that don't have a companyId AND are not currently in the list being edited
    const currentListIds = (this.editingId ? this.currentStores : this.tempStores).map(s => s.id!);
    
    this.availableStores = this.allStores.filter(s => 
      (!s.companyId || (this.editingId && s.companyId === this.editingId)) && 
      !currentListIds.includes(s.id!)
    );
  }

  ngOnDestroy() {
    this.ownershipsSub?.unsubscribe();
    this.allStoresSub?.unsubscribe();
  }

  checkIfFeeCollectionDisabled(): boolean {
    if (!this.newCompany || !this.newCompany.brand) {
      return false;
    }

    if (this.newCompany.isFeeCollection) {
      return false;
    }

    return this.companies.some(c => 
      c.brand === this.newCompany.brand && 
      c.isFeeCollection === true &&
      c.id !== this.editingId
    );
  }
  
  @HostListener('document:keydown.escape', ['$event'])
  onEscKey(event: Event) {
    if (this.isUploadModalOpen && !this.uploadingFile) {
      this.closeUploadModal();
      return;
    }
    if (this.isModalOpen) {
      this.cancelEdit();
    }
  }

  @HostListener('document:keydown.enter', ['$event'])
  onEnterKey(event: Event) {
    if (!this.isModalOpen) return;

    const target = event.target as HTMLElement;
    const isInput = target.tagName === 'INPUT' || target.tagName === 'SELECT';

    if (isInput) {
      if (this.newOwnership.ownerId) {
        this.addOwnership();
      }
      if (this.newModalStore.name) {
        this.addStoreToModal();
      }
      if (this.newAdjustment.description) {
        this.addAdjustment();
      }
    }
  }

  // --- Company CRUD ---

  openAddModal() {
    this.cancelEdit(); // Resets state
    this.isModalOpen = true;
    this.isOwnershipExpanded = true;
    this.isStoresExpanded = true;
    this.isSisRatesExpanded = true;
    this.isBrokerReportExpanded = true;
    this.isAdjustmentsExpanded = true;
    this.ownershipErrorMessage = null;
    this.generalErrorMessage = null;
  }

  async saveCompany() {
    this.isSaving = true;
    this.errorMessage = null;
    this.ownershipErrorMessage = null;
    this.generalErrorMessage = null;

    // Validate Ownership & Profit Share Totals
    let totalOwnership = 0;
    let totalProfitShare = 0;

    if (this.editingId) {
      this.currentOwnerships.forEach(o => {
        totalOwnership += Number(o.ownershipPercent || 0);
        totalProfitShare += Number(o.profitSharePercent || 0);
      })
    } else {
      this.tempOwnerships.forEach(o => {
        totalOwnership += Number(o.ownershipPercent || 0);
        totalProfitShare += Number(o.profitSharePercent || 0);
      });
    }

    if (totalOwnership !== 100 || totalProfitShare !== 100) {
      this.ownershipErrorMessage = `Error: Total Ownership (${totalOwnership}%) and Profit Share (${totalProfitShare}%) must both equal 100%.`;
      this.isOwnershipExpanded = true;
      this.generalErrorMessage = 'There are errors that need to be corrected.';
      this.isSaving = false;
      return;
    }

    if (!this.newCompany.company || !this.newCompany.brand || !this.newCompany.pocName || !this.newCompany.email || this.newCompany.salesTax == null || this.newCompany.salesTax === '') {
      this.generalErrorMessage = 'Please fill in all required fields (*).';
      this.isSaving = false;
      return;
    }

    try {
    const col = collection(this.firestore, 'company');
    
    if (this.editingId) {
      const docRef = doc(this.firestore, 'company', this.editingId);
      const dataToUpdate = {
        company: this.newCompany.company,
        brand: this.newCompany.brand || '',
        pocName: this.newCompany.pocName,
        email: this.newCompany.email,
        phone: this.newCompany.phone,
        ein: this.newCompany.ein || '',
        fedexNumber: this.newCompany.fedexNumber || '',
        address: this.newCompany.address,
        salesTax: this.newCompany.salesTax || 0,
        reserveRate: this.newCompany.reserveRate || 0,
        kcegOwned: this.newCompany.kcegOwned || false,
        sis: this.newCompany.sis || false,
        brokerReport: this.newCompany.brokerReport || false,
        brokerName: this.newCompany.brokerName || '',
        brokerLocation: this.newCompany.brokerLocation || '',
        brokerCcRate: this.newCompany.brokerCcRate || 0,
        brokerProfitShare: this.newCompany.brokerProfitShare || 0,
        sisCcRate: this.newCompany.sisCcRate || 0,
        sisProfitShare: this.newCompany.sisProfitShare || 0,
        mallEmail: this.newCompany.mallEmail || '',
        isFeeCollection: this.newCompany.isFeeCollection || false,
        adjustments: this.currentAdjustments
      };
      await updateDoc(docRef, dataToUpdate);

      // Save ownership updates
      for (const item of this.currentOwnerships) {
        if (item.id) {
          const itemRef = doc(this.firestore, 'company_ownerships', item.id);
          await updateDoc(itemRef, {
            ownershipPercent: item.ownershipPercent,
            profitSharePercent: item.profitSharePercent
          });
        }
      }

      // Save Stores Updates
      // 1. Unassign removed stores
      for (const id of this.storesToDelete) {
        await updateDoc(doc(this.firestore, 'stores', id), { 
          companyId: null,
          companyIds: arrayRemove(this.editingId)
        });
      }
      // 2. Assign new stores
      for (const store of this.currentStores) {
        await updateDoc(doc(this.firestore, 'stores', store.id!), { 
          companyId: this.editingId,
          companyIds: arrayUnion(this.editingId)
        });
      }

      this.editingId = null;
    } else {
      const docRef = await addDoc(col, { ...this.newCompany, adjustments: this.tempAdjustments, createdAt: new Date() });
      
      // Save temp ownerships for the new company
      if (this.tempOwnerships.length > 0) {
        const ownershipsCol = collection(this.firestore, 'company_ownerships');
        for (const owner of this.tempOwnerships) {
          await addDoc(ownershipsCol, {
            companyId: docRef.id,
            ownerId: owner.ownerId,
            ownershipPercent: owner.ownershipPercent,
            profitSharePercent: owner.profitSharePercent
          });
        }
      }

      // Save temp stores for the new company
      if (this.tempStores.length > 0) {
        for (const store of this.tempStores) {
          await updateDoc(doc(this.firestore, 'stores', store.id!), { 
            companyId: docRef.id,
            companyIds: arrayUnion(docRef.id)
          });
        }
      }
    }
    
    this.newCompany = { company: '', brand: '', pocName: '', email: '', phone: '', ein: '', fedexNumber: '', address: '', salesTax: 0, reserveRate: 0, kcegOwned: false, sis: false, brokerReport: false, brokerName: '', brokerLocation: '', brokerCcRate: null, brokerProfitShare: null, sisCcRate: null, sisProfitShare: null, mallEmail: '', isFeeCollection: false };
    this.tempOwnerships = [];
    this.tempStores = [];
    this.storesToDelete = [];
    this.currentAdjustments = [];
    this.tempAdjustments = [];
    this.errorMessage = null;
    this.isModalOpen = false;
    } finally {
      this.isSaving = false;
    }
  }

  async editCompany(company: any) {
    this.newCompany = { ...company };
    this.editingId = company.id;
    this.tempOwnerships = []; // Clear any pending adds
    this.tempStores = [];
    this.storesToDelete = [];
    this.errorMessage = null;
    this.updateAvailableStores();
    this.ownershipErrorMessage = null;
    this.generalErrorMessage = null;
    this.isModalOpen = true;
    this.isOwnershipExpanded = true;
    this.isStoresExpanded = true;
    this.isSisRatesExpanded = true;
    this.isBrokerReportExpanded = true;
    this.isAdjustmentsExpanded = true;
    this.currentAdjustments = company.adjustments ? JSON.parse(JSON.stringify(company.adjustments)) : [];

    // Load ownerships for this company
    this.ownershipsSub?.unsubscribe();
    const ownershipsCol = collection(this.firestore, 'company_ownerships');
    const q = query(ownershipsCol, where('companyId', '==', company.id));
    this.ownershipsSub = collectionData(q, { idField: 'id' }).subscribe(data => {
      this.currentOwnerships = data.sort((a, b) => {
        const nameA = this.getOwnerName(a['ownerId']) || '';
        const nameB = this.getOwnerName(b['ownerId']) || '';
        return nameA.localeCompare(nameB);
      });
    });

    // Load stores for this company
    const storesCol = collection(this.firestore, 'stores');
    
    const qLegacy = query(storesCol, where('companyId', '==', company.id));
    const qArray = query(storesCol, where('companyIds', 'array-contains', company.id));

    const [snapLegacy, snapArray] = await Promise.all([
      getDocs(qLegacy),
      getDocs(qArray)
    ]);

    const uniqueStores = new Map<string, Store>();
    const addToMap = (d: any) => {
      if (!d.data().isDeleted) uniqueStores.set(d.id, { id: d.id, ...d.data() } as Store);
    };
    snapLegacy.forEach(addToMap);
    snapArray.forEach(addToMap);
    this.currentStores = Array.from(uniqueStores.values()).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    this.updateAvailableStores();
  }

  cancelEdit() {
    this.newCompany = { company: '', brand: '', pocName: '', email: '', phone: '', ein: '', fedexNumber: '', address: '', salesTax: 0, reserveRate: 0, kcegOwned: false, sis: false, brokerReport: false, brokerName: '', brokerLocation: '', brokerCcRate: null, brokerProfitShare: null, sisCcRate: null, sisProfitShare: null, mallEmail: '', isFeeCollection: false };
    this.editingId = null;
    this.ownershipsSub?.unsubscribe();
    this.currentOwnerships = [];
    this.currentStores = [];
    this.tempOwnerships = [];
    this.tempStores = [];
    this.currentAdjustments = [];
    this.tempAdjustments = [];
    this.storesToDelete = [];
    this.errorMessage = null;
    this.ownershipErrorMessage = null;
    this.generalErrorMessage = null;
    this.isModalOpen = false;
    this.updateAvailableStores();
  }

  async deleteCompany(id: string) {
    // Check for associated active stores
    const storesCol = collection(this.firestore, 'stores');
    const qStores = query(storesCol, where('companyId', '==', id));
    const storesSnapshot = await getDocs(qStores);
    
    // Check if any store is NOT deleted (active)
    const hasActiveStores = storesSnapshot.docs.some(doc => !doc.data()['isDeleted']);

    if (hasActiveStores) {
      alert('Cannot delete company. Please remove or reassign all associated stores first.');
      return;
    }

    if (confirm('Are you sure you want to delete this company?')) {
      // Soft delete
      const docRef = doc(this.firestore, 'company', id);
      await updateDoc(docRef, { isDeleted: true });
    }
  }

  // --- Store Logic ---

  toggleExpand(companyId: string) {
    this.isAddingStore = false;
    this.newStore = { name: '', location: '' };
    if (this.expandedCompanyId === companyId) {
      this.expandedCompanyId = null;
      this.companyStores$ = null;
      this.companyOwnerships$ = null;
    } else {
      this.expandedCompanyId = companyId;
      const storesCol = collection(this.firestore, 'stores');
      
      const qLegacy = query(storesCol, where('companyId', '==', companyId));
      const qArray = query(storesCol, where('companyIds', 'array-contains', companyId));
      
      const legacy$ = collectionData(qLegacy, { idField: 'id' });
      const array$ = collectionData(qArray, { idField: 'id' });

      this.companyStores$ = combineLatest([legacy$, array$]).pipe(
        map(([legacyStores, arrayStores]) => {
          const combined = [...(legacyStores as any[]), ...(arrayStores as any[])];
          const unique = new Map();
          combined.forEach(s => {
            if (!s.isDeleted) unique.set(s.id, s);
          });
          return Array.from(unique.values()) as Store[];
        })
      );

      const ownershipsCol = collection(this.firestore, 'company_ownerships');
      const qOwnership = query(ownershipsCol, where('companyId', '==', companyId));
      this.companyOwnerships$ = collectionData(qOwnership, { idField: 'id' });
    }
  }

  async addStore(companyId: string) {
    if (!this.newStore.name) return;
    const storesCol = collection(this.firestore, `company/${companyId}/stores`);
    await addDoc(storesCol, this.newStore);
    this.newStore = { name: '', location: '' };
    this.isAddingStore = false;
  }

  async deleteStore(companyId: string, storeId: string) {
    if (confirm('Delete this store?')) {
      const docRef = doc(this.firestore, `company/${companyId}/stores/${storeId}`);
      await deleteDoc(docRef);
    }
  }

  // --- Modal Store Logic ---

  addStoreToModal() {
    if (!this.newModalStore.id) return;

    const selectedStore = this.allStores.find(s => s.id === this.newModalStore.id);
    if (!selectedStore) return;

    if (this.editingId) {
      this.currentStores.push({ ...selectedStore });
      this.storesToDelete = this.storesToDelete.filter(id => id !== selectedStore.id);
    } else {
      this.tempStores.push({ ...selectedStore });
    }
    this.newModalStore = { name: '', location: '' }; // Reset
    this.updateAvailableStores();
  }

  deleteStoreFromModal(storeId: string | null, index: number) {
    if (this.editingId) {
      if (storeId) {
        this.storesToDelete.push(storeId);
      }
      this.currentStores.splice(index, 1);
    } else {
      this.tempStores.splice(index, 1);
    }
    this.updateAvailableStores();
  }

  // --- Ownership Logic ---

  async addOwnership() {
    this.ownershipErrorMessage = null;
    if (!this.newOwnership.ownerId) return;

    const listToCheck = this.editingId ? this.currentOwnerships : this.tempOwnerships;
    const exists = listToCheck.some(item => item.ownerId === this.newOwnership.ownerId);

    if (exists) {
      this.ownershipErrorMessage = 'Error: This owner has already been added.';
      return;
    }
    
    if (this.editingId) {
      const col = collection(this.firestore, 'company_ownerships');
      await addDoc(col, {
        companyId: this.editingId,
        ownerId: this.newOwnership.ownerId,
        ownershipPercent: this.newOwnership.ownershipPercent,
        profitSharePercent: this.newOwnership.profitSharePercent
      });
    } else {
      this.tempOwnerships.push({ ...this.newOwnership });
    }

    // Reset form but keep percentages 0
    this.newOwnership = { ownerId: '', ownershipPercent: 0, profitSharePercent: 0 };
  }

  async deleteOwnership(id: string | null, index?: number) {
    if (id) {
      if (confirm('Remove this ownership record?')) {
        await deleteDoc(doc(this.firestore, 'company_ownerships', id));
      }
    } else if (typeof index === 'number') {
      this.tempOwnerships.splice(index, 1);
    }
  }

  getOwnerName(id: string): string {
    const owner = this.owners.find(o => o.id === id);
    return owner ? owner.name : id;
  }

  getBrandName(id: string): string {
    const brand = this.brands.find(b => b.id === id);
    return brand ? brand.name : id;
  }

  addAdjustment() {
    if (!this.newAdjustment.description) return;
    if (this.editingId) {
      this.currentAdjustments.push({ ...this.newAdjustment });
    } else {
      this.tempAdjustments.push({ ...this.newAdjustment });
    }
    this.newAdjustment = { description: '', amount: null };
  }

  deleteAdjustment(index: number) {
    if (this.editingId) {
      this.currentAdjustments.splice(index, 1);
    } else {
      this.tempAdjustments.splice(index, 1);
    }
  }

  // --- File Upload Logic ---

  openUploadModal(company: any) {
    this.currentUploadCompany = company;
    this.selectedFiles = [];
    this.uploadedFileUrl = null;
    this.deletingFileIndex = null;
    this.uploadProgress = 0;
    this.uploadingFile = false;
    this.isUploadModalOpen = true;
  }

  closeUploadModal() {
    if (this.uploadingFile) return;
    this.isUploadModalOpen = false;
    this.currentUploadCompany = null;
    this.selectedFiles = [];
  }

  onFileSelected(event: any) {
    if (event.target.files) {
      this.selectedFiles = Array.from(event.target.files);
    }
  }

  async uploadFile() {
    if (this.selectedFiles.length === 0 || !this.currentUploadCompany) return;

    this.uploadingFile = true;
    this.uploadProgress = 0;
    this.uploadedFileUrl = null;
    
    try {
      const companyId = this.currentUploadCompany.id;
      let currentFiles = this.currentUploadCompany.files || [];
      
      const progressMap = new Map<string, number>();
      let overallTotal = this.selectedFiles.reduce((acc, file) => acc + file.size, 0);
      if (overallTotal === 0) overallTotal = 1;

      const uploadPromises = this.selectedFiles.map(file => {
        return new Promise<void>((resolve, reject) => {
          const timestamp = Date.now();
          const fileName = file.name;
          const filePath = `company_files/${companyId}/${timestamp}_${fileName}`;
          const storageRef = ref(this.storage, filePath);

          const uploadTask = uploadBytesResumable(storageRef, file);

          uploadTask.on('state_changed', 
            (snapshot) => {
              progressMap.set(fileName, snapshot.bytesTransferred);
              let currentTransferred = 0;
              progressMap.forEach(transferred => currentTransferred += transferred);
              this.uploadProgress = Math.min(100, Math.round((currentTransferred / overallTotal) * 100));
            }, 
            (error) => {
              console.error('Upload failed for', fileName, error);
              reject(error);
            }, 
            async () => {
              const url = await getDownloadURL(uploadTask.snapshot.ref);
              currentFiles.push({
                url: url,
                storagePath: filePath,
                name: fileName
              });
              resolve();
            }
          );
        });
      });

      await Promise.all(uploadPromises);

      const docRef = doc(this.firestore, 'company', companyId);
      await updateDoc(docRef, { files: currentFiles, updatedAt: new Date() });

      this.currentUploadCompany.files = currentFiles;
      this.uploadingFile = false;
      this.selectedFiles = [];
      this.uploadedFileUrl = 'done';
      
      const input = document.getElementById('companyFileUploadInput') as HTMLInputElement;
      if (input) input.value = '';
    } catch (error) {
      console.error('Error starting upload:', error);
      this.uploadingFile = false;
      alert('An error occurred during the upload.');
    }
  }

  async deleteFile(index: number) {
    if (!this.currentUploadCompany || !this.currentUploadCompany.files) return;
    if (!confirm('Are you sure you want to delete this attached file?')) return;
    
    this.deletingFileIndex = index;
    const attachment = this.currentUploadCompany.files[index];
    
    try {
      if (attachment.storagePath) {
        const storageRef = ref(this.storage, attachment.storagePath);
        await deleteObject(storageRef);
      }
      
      this.currentUploadCompany.files.splice(index, 1);
      const docRef = doc(this.firestore, 'company', this.currentUploadCompany.id);
      await updateDoc(docRef, { files: this.currentUploadCompany.files, updatedAt: new Date() });
      
    } catch (error) {
      console.error('Error deleting file:', error);
      alert('Failed to delete file.');
    } finally {
      this.deletingFileIndex = null;
    }
  }

  // --- Sorting ---
  onSort(column: string) {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.sortCompanies();
  }

  private sortCompanies() {
    if (!this.companies || this.companies.length === 0) {
      return;
    }

    this.companies.sort((a, b) => {
      let valA = a[this.sortColumn];
      let valB = b[this.sortColumn];

      if (this.sortColumn === 'brand') {
        valA = this.getBrandName(valA);
        valB = this.getBrandName(valB);
      }

      let comparison = 0;
      
      if (valA == null && valB == null) {
        comparison = 0;
      } else if (valA == null) {
        comparison = -1;
      } else if (valB == null) {
        comparison = 1;
      } else if (typeof valA === 'number' && typeof valB === 'number') {
        comparison = valA - valB;
      } else if (typeof valA === 'boolean' && typeof valB === 'boolean') {
        comparison = (valA === valB) ? 0 : valA ? -1 : 1;
      } else {
        comparison = String(valA).toLowerCase().localeCompare(String(valB).toLowerCase());
      }

      return this.sortDirection === 'asc' ? comparison : -comparison;
    });
  }
}
